import clsx from "clsx";
import React from "react";

import { FONT_FAMILY, MIME_TYPES, randomId } from "@excalidraw/common";
import { newImageElement, newTextElement } from "@excalidraw/element";

import type { ExcalidrawElement, FileId } from "@excalidraw/element/types";

import { editorJotaiStore } from "../editor-jotai";
import { useLibraryCache, useLibraryItemSvg } from "../hooks/useLibraryItemSvg";

import { DrawLabIconsIcon } from "./icons";
import { drawLabIconPickerOpenAtom } from "./DrawLabIconPicker.state";

import "./DrawLabIconPicker.scss";

import type { AppClassProperties, BinaryFiles, DataURL } from "../types";

type PickerTab = "tabler" | "sketch" | "icon";

type TablerEntry = {
  id: string;
  name: string;
  label: string;
  style: "outline" | "filled";
  file: string;
};

type SketchLibraryEntry = {
  id: string;
  label: string;
  file: string;
};

type SketchItem = {
  id: string;
  name: string;
  source: string;
  elements: readonly ExcalidrawElement[];
};

type IconEntry = {
  t: string;
  y: string;
  n: string;
  g: string;
};

type IconSection = {
  type: string;
  label: string;
  items: IconEntry[];
};

const RESULT_LIMIT = 96;

const ICON_INITIAL_VISIBLE = 80;
const ICON_LOAD_MORE_STEP = 80;

/** English stopwords + generic UI words that carry no search signal. */
const ICON_STOPWORDS = new Set([
  "a",
  "an",
  "the",
  "and",
  "or",
  "but",
  "to",
  "of",
  "in",
  "on",
  "at",
  "for",
  "with",
  "from",
  "is",
  "are",
  "be",
  "it",
  "this",
  "that",
  "these",
  "those",
  "my",
  "your",
  "our",
  "me",
  "you",
  "i",
  "we",
  "they",
  "he",
  "she",
  "as",
  "by",
  "so",
  "do",
  "does",
  "can",
  "will",
  "please",
  "give",
  "show",
  "find",
  "get",
  "want",
  "need",
  "looking",
  "search",
  "some",
  "any",
  "all",
  "one",
  "two",
  "good",
  "nice",
  "best",
  "emoji",
  "icon",
  "emoticon",
  "kaomoji",
  "symbol",
  "symbols",
  "text",
  "face",
  "faces",
  "copy",
  "paste",
  "thing",
  "stuff",
]);

/** Query synonym → canonical term. Ported từ icon.quoc.app search.ts. */
const ICON_QUERY_ALIASES: Record<string, string> = {
  heart: "love",
  hearts: "love",
  romance: "love",
  romantic: "love",
  valentine: "love",
  kiss: "love",
  kisses: "love",
  kissing: "love",
  hug: "love",
  hugs: "love",
  hugging: "love",
  couple: "love",
  smile: "happy",
  smiling: "happy",
  smiley: "happy",
  smiles: "happy",
  joy: "happy",
  joyful: "happy",
  laugh: "happy",
  laughing: "happy",
  laughs: "happy",
  grin: "happy",
  grinning: "happy",
  lol: "happy",
  haha: "happy",
  hehe: "happy",
  excited: "happy",
  wink: "happy",
  winking: "happy",
  cheer: "happy",
  cry: "sad",
  crying: "sad",
  tear: "sad",
  tears: "sad",
  lonely: "sad",
  worried: "sad",
  pensive: "sad",
  mad: "angry",
  rage: "angry",
  furious: "angry",
  pout: "angry",
  pouting: "angry",
  shocked: "surprise",
  wow: "surprise",
  astonished: "surprise",
  omg: "surprise",
  idea: "thinking",
  hmm: "thinking",
  blush: "shy",
  blushing: "shy",
  flushed: "shy",
  sleepy: "sleep",
  zzz: "sleep",
  tired: "sleep",
  sunglasses: "cool",
  doctor: "care",
  nurse: "care",
  hospital: "care",
  medical: "care",
  sick: "care",
  people: "person",
  human: "person",
  man: "person",
  woman: "person",
  boy: "person",
  girl: "person",
  baby: "person",
  child: "person",
  family: "person",
  finger: "hand",
  clap: "hand",
  praying: "hand",
  fist: "hand",
  punch: "hand",
  creature: "animal",
  pet: "animal",
  kitty: "cat",
  kitten: "cat",
  cats: "cat",
  feline: "cat",
  puppy: "dog",
  puppies: "dog",
  dogs: "dog",
  paw: "dog",
  birds: "bird",
  duck: "bird",
  chick: "bird",
  whale: "fish",
  dolphin: "fish",
  shark: "fish",
  bunny: "animal",
  monkey: "animal",
  frog: "animal",
  forest: "nature",
  mountain: "nature",
  river: "nature",
  ocean: "nature",
  plant: "tree",
  plants: "tree",
  leaf: "tree",
  leaves: "tree",
  seedling: "tree",
  wood: "tree",
  rose: "flower",
  tulip: "flower",
  blossom: "flower",
  bouquet: "flower",
  rainy: "weather",
  cloudy: "weather",
  sun: "weather",
  sunny: "weather",
  snow: "weather",
  storm: "weather",
  thunder: "weather",
  lightning: "weather",
  umbrella: "weather",
  wind: "weather",
  windy: "weather",
  rainbow: "weather",
  comet: "weather",
  fruit: "food",
  coffee: "food",
  cake: "food",
  pizza: "food",
  burger: "food",
  beer: "food",
  wine: "food",
  hungry: "food",
  car: "travel",
  train: "travel",
  plane: "travel",
  ship: "travel",
  boat: "travel",
  rocket: "travel",
  bus: "travel",
  bike: "travel",
  clock: "time",
  watch: "time",
  hour: "time",
  alarm: "time",
  dollar: "money",
  dollars: "money",
  cash: "money",
  coin: "money",
  coins: "money",
  currency: "money",
  yen: "money",
  euro: "money",
  pound: "money",
  song: "music",
  guitar: "music",
  piano: "music",
  drum: "music",
  ball: "sport",
  soccer: "sport",
  football: "sport",
  trophy: "sport",
  country: "flag",
  nation: "flag",
  flags: "flag",
  arrows: "arrow",
  direction: "arrow",
  sparkle: "star",
  sparkles: "star",
  stars: "star",
  magic: "star",
  tick: "check",
  ok: "check",
  cancel: "cross",
  delete: "cross",
  alert: "warning",
  danger: "warning",
  prohibited: "warning",
  number: "math",
  numbers: "math",
  plus: "math",
  minus: "math",
  sword: "weapon",
  gun: "weapon",
};

/** Tokenize query: lowercase, split words, remove stopwords + <2 char tokens. */
const iconTokenize = (query: string): string[] => {
  const tokens = query.toLowerCase().match(/[a-z0-9]+/g) ?? [];
  return tokens.filter((tok) => tok.length >= 2 && !ICON_STOPWORDS.has(tok));
};

const getAssetUrl = (path: string) => {
  const base = import.meta.env.BASE_URL || "/";
  const normalizedBase = base.endsWith("/") ? base : `${base}/`;
  return `${normalizedBase}drawlab-icon-picker/${path}`;
};

const normalizeSearch = (value: string) => value.trim().toLowerCase();

const svgToDataURL = (svg: string): DataURL => {
  const bytes = new TextEncoder().encode(svg);
  let binary = "";

  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return `data:image/svg+xml;base64,${window.btoa(binary)}` as DataURL;
};

const getSvgDimensions = (svg: string) => {
  const viewBox = svg.match(/viewBox=["']([^"']+)["']/i)?.[1];

  if (viewBox) {
    const [, , width, height] = viewBox
      .split(/[\s,]+/)
      .map((value) => Number(value));

    if (Number.isFinite(width) && Number.isFinite(height) && height > 0) {
      const target = 96;
      return {
        width: Math.round(target * (width / height)),
        height: target,
      };
    }
  }

  return { width: 96, height: 96 };
};

const createTablerImageElement = (entry: TablerEntry, svg: string) => {
  const fileId = `drawlab-${entry.id}-${randomId()}` as FileId;
  const { width, height } = getSvgDimensions(svg);

  const element = newImageElement({
    type: "image",
    x: 0,
    y: 0,
    width,
    height,
    fileId,
    status: "saved",
    scale: [1, 1],
    strokeColor: "transparent",
    backgroundColor: "transparent",
  });

  const files: BinaryFiles = {
    [fileId]: {
      id: fileId,
      mimeType: MIME_TYPES.svg,
      dataURL: svgToDataURL(svg),
      created: Date.now(),
      lastRetrieved: Date.now(),
    },
  };

  return { element, files };
};

const extractSketchItems = (
  library: unknown,
  source: SketchLibraryEntry,
): SketchItem[] => {
  if (!library || typeof library !== "object") {
    return [];
  }

  const payload = library as {
    libraryItems?: unknown[];
    library?: unknown[];
  };

  const rawItems = payload.libraryItems || payload.library || [];

  return rawItems.flatMap((rawItem, index) => {
    if (Array.isArray(rawItem)) {
      return [
        {
          id: `${source.id}-${index}`,
          name: `${source.label} ${index + 1}`,
          source: source.label,
          elements: rawItem as readonly ExcalidrawElement[],
        },
      ];
    }

    if (rawItem && typeof rawItem === "object" && "elements" in rawItem) {
      const item = rawItem as {
        id?: string;
        name?: string;
        elements?: readonly ExcalidrawElement[];
      };

      if (!item.elements?.length) {
        return [];
      }

      return [
        {
          id: item.id || `${source.id}-${index}`,
          name: item.name || `${source.label} ${index + 1}`,
          source: source.label,
          elements: item.elements,
        },
      ];
    }

    return [];
  });
};

const SketchItemThumbnail = ({
  id,
  elements,
}: {
  id: string;
  elements: readonly ExcalidrawElement[];
}) => {
  const { svgCache } = useLibraryCache();
  const svg = useLibraryItemSvg(id, elements, svgCache);
  const containerRef = React.useRef<HTMLSpanElement>(null);

  React.useEffect(() => {
    const node = containerRef.current;
    if (!node || !svg) {
      return;
    }
    node.innerHTML = "";
    node.appendChild(svg);
  }, [svg]);

  return <span className="DrawLabIconPicker__sketchThumb" ref={containerRef} />;
};

export const DrawLabIconPicker = ({ app }: { app: AppClassProperties }) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  // Sync với shared atom để phím tắt `/` (xử lý ở App) cũng mở/đóng được.
  React.useEffect(() => {
    return editorJotaiStore.sub(drawLabIconPickerOpenAtom, () => {
      const next = editorJotaiStore.get(drawLabIconPickerOpenAtom);
      setIsOpen((open) => (open === next ? open : next));
    });
  }, []);
  const [activeTab, setActiveTab] = React.useState<PickerTab>("tabler");
  const [query, setQuery] = React.useState("");
  const [tablerIndex, setTablerIndex] = React.useState<TablerEntry[] | null>(
    null,
  );
  const [sketchItems, setSketchItems] = React.useState<SketchItem[] | null>(
    null,
  );
  const [iconData, setIconData] = React.useState<IconEntry[] | null>(null);
  const [iconVisibleCount, setIconVisibleCount] =
    React.useState(ICON_INITIAL_VISIBLE);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const close = React.useCallback(() => {
    editorJotaiStore.set(drawLabIconPickerOpenAtom, false);
    setIsOpen(false);
  }, []);

  React.useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        close();
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      }
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    // Reset search + active tab mỗi lần mở lại.
    setQuery("");
    setActiveTab("tabler");
    requestAnimationFrame(() => searchInputRef.current?.focus());

    const positionPanel = () => {
      const trigger = rootRef.current?.querySelector<HTMLElement>(
        ".DrawLabIconPicker__trigger",
      );
      const panel = rootRef.current?.querySelector<HTMLElement>(
        ".DrawLabIconPicker__panel",
      );
      if (!trigger || !panel) {
        return;
      }

      const tr = trigger.getBoundingClientRect();
      if (!tr.width || !tr.height) {
        return;
      }

      const gap = 8;
      const viewportMargin = 8;
      const vw = window.visualViewport?.width || window.innerWidth;
      const vh = window.visualViewport?.height || window.innerHeight;

      // Reset các cạnh trước khi đo. Không dùng lại tọa độ cũ vì toolbar có thể
      // vừa reflow từ top sang bottom ở breakpoint responsive.
      panel.style.inset = "auto";
      panel.style.maxWidth = `calc(100vw - ${viewportMargin * 2}px)`;
      panel.style.position = "fixed";
      panel.style.left = "0px";
      panel.style.top = "0px";

      const pw = panel.offsetWidth;
      const ph = panel.offsetHeight;

      let left = tr.right - pw;
      if (left < viewportMargin) {
        left = tr.left;
      }
      left = Math.min(
        Math.max(viewportMargin, left),
        Math.max(viewportMargin, vw - pw - viewportMargin),
      );

      const belowTop = tr.bottom + gap;
      const aboveTop = tr.top - ph - gap;
      const availableBelow = vh - tr.bottom - gap - viewportMargin;
      const availableAbove = tr.top - gap - viewportMargin;

      let top =
        availableBelow >= ph || availableBelow >= availableAbove
          ? belowTop
          : aboveTop;
      top = Math.min(
        Math.max(viewportMargin, top),
        Math.max(viewportMargin, vh - ph - viewportMargin),
      );

      panel.style.left = `${left}px`;
      panel.style.top = `${top}px`;
    };

    const resizeObserver = new ResizeObserver(positionPanel);
    const panel = rootRef.current?.querySelector<HTMLElement>(
      ".DrawLabIconPicker__panel",
    );
    const trigger = rootRef.current?.querySelector<HTMLElement>(
      ".DrawLabIconPicker__trigger",
    );
    if (panel) {
      resizeObserver.observe(panel);
    }
    if (trigger) {
      resizeObserver.observe(trigger);
    }

    const onResize = () => positionPanel();
    window.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("resize", onResize);

    // Đợi thêm 2 frame để bắt đúng vị trí sau khi responsive layout reflow xong.
    const rafs: number[] = [];
    rafs.push(requestAnimationFrame(positionPanel));
    rafs.push(
      requestAnimationFrame(() => {
        rafs.push(requestAnimationFrame(positionPanel));
      }),
    );

    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("resize", onResize);
      resizeObserver.disconnect();
      rafs.forEach((raf) => cancelAnimationFrame(raf));
    };
  }, [isOpen, close]);

  React.useEffect(() => {
    if (!isOpen || activeTab !== "tabler" || tablerIndex) {
      return;
    }

    let cancelled = false;

    const loadTablerIndex = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(getAssetUrl("tabler-index.json"));
        if (!response.ok) {
          throw new Error("Không tải được Tabler index");
        }
        const entries = (await response.json()) as TablerEntry[];
        if (!cancelled) {
          setTablerIndex(entries);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Lỗi tải Tabler");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadTablerIndex();

    return () => {
      cancelled = true;
    };
  }, [activeTab, isOpen, tablerIndex]);

  React.useEffect(() => {
    if (!isOpen || activeTab !== "sketch" || sketchItems) {
      return;
    }

    let cancelled = false;

    const loadSketchItems = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const indexResponse = await fetch(getAssetUrl("sketch-index.json"));
        if (!indexResponse.ok) {
          throw new Error("Không tải được Sketch index");
        }

        const libraries = (await indexResponse.json()) as SketchLibraryEntry[];

        const allItems = (
          await Promise.all(
            libraries.map(async (library) => {
              const response = await fetch(getAssetUrl(library.file));
              if (!response.ok) {
                throw new Error(`Không tải được ${library.label}`);
              }
              return extractSketchItems(await response.json(), library);
            }),
          )
        ).flat();

        if (!cancelled) {
          setSketchItems(allItems);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Lỗi tải Sketch");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadSketchItems();

    return () => {
      cancelled = true;
    };
  }, [activeTab, isOpen, sketchItems]);

  React.useEffect(() => {
    if (!isOpen || activeTab !== "icon" || iconData) {
      return;
    }

    let cancelled = false;

    const loadIconData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(getAssetUrl("icon-data.json"));
        if (!response.ok) {
          throw new Error("Không tải được Icon data");
        }
        const entries = (await response.json()) as IconEntry[];
        if (!cancelled) {
          setIconData(entries);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Lỗi tải Icon data");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadIconData();

    return () => {
      cancelled = true;
    };
  }, [activeTab, isOpen, iconData]);

  // Reset progressive render count khi đổi tab hoặc search.
  React.useEffect(() => {
    setIconVisibleCount(ICON_INITIAL_VISIBLE);
  }, [activeTab, query]);

  const filteredTablerEntries = React.useMemo(() => {
    const normalizedQuery = normalizeSearch(query);
    const entries = tablerIndex || [];

    if (!normalizedQuery) {
      return entries.slice(0, RESULT_LIMIT);
    }

    return entries
      .filter((entry) => {
        const haystack = `${entry.name} ${entry.label} ${entry.style}`;
        return haystack.toLowerCase().includes(normalizedQuery);
      })
      .slice(0, RESULT_LIMIT);
  }, [query, tablerIndex]);

  const filteredSketchItems = React.useMemo(() => {
    const normalizedQuery = normalizeSearch(query);
    const items = sketchItems || [];

    if (!normalizedQuery) {
      return items.slice(0, RESULT_LIMIT);
    }

    return items
      .filter((item) => {
        const haystack = `${item.name} ${item.source}`;
        return haystack.toLowerCase().includes(normalizedQuery);
      })
      .slice(0, RESULT_LIMIT);
  }, [query, sketchItems]);

  const iconSections = React.useMemo<IconSection[]>(() => {
    if (!iconData) {
      return [];
    }

    const normalizedQuery = normalizeSearch(query);
    const buckets: Record<string, IconEntry[]> = {
      emoji: [],
      symbol: [],
      kaomoji: [],
    };

    if (!normalizedQuery) {
      // Không search: hiện tất cả theo thứ tự gốc.
      for (const item of iconData) {
        if (buckets[item.y]) {
          buckets[item.y].push(item);
        }
      }
    } else {
      // Tokenize + synonym expansion (ported từ icon.quoc.app).
      const tokens = iconTokenize(query);
      // Nếu query chỉ có stopword/short token, fallback substring thô.
      const searchTerms =
        tokens.length > 0
          ? new Set(
              tokens.flatMap((tok) => [tok, ICON_QUERY_ALIASES[tok] ?? tok]),
            )
          : new Set([normalizedQuery]);

      for (const item of iconData) {
        const nameLower = item.n.toLowerCase();
        const tagsLower = item.g.toLowerCase();
        let matched = false;
        for (const term of searchTerms) {
          if (nameLower.includes(term) || tagsLower.includes(term)) {
            matched = true;
            break;
          }
        }
        if (matched && buckets[item.y]) {
          buckets[item.y].push(item);
        }
      }
    }

    return [
      { type: "emoji", label: "Emoji", items: buckets.emoji },
      { type: "symbol", label: "Symbols", items: buckets.symbol },
      { type: "kaomoji", label: "Kaomoji", items: buckets.kaomoji },
    ].filter((section) => section.items.length > 0);
  }, [query, iconData]);

  const insertTablerIcon = async (entry: TablerEntry) => {
    setError(null);

    try {
      const response = await fetch(getAssetUrl(entry.file));
      if (!response.ok) {
        throw new Error(`Không tải được ${entry.label}`);
      }

      const rawSvg = await response.text();
      // SVG Tabler dùng `stroke="currentColor"`. Khi Excalidraw rasterize
      // image element (qua Image/createImageBitmap), currentColor không có
      // context CSS → resolve về transparent → icon trống trơn, chỉ thấy khi
      // stretch. Substitute bằng màu cụ thể theo theme để render đúng.
      const theme = app.state?.theme ?? "light";
      const color = theme === "dark" ? "#f5f5f4" : "#1f1f1f";
      const svg = rawSvg.replaceAll("currentColor", color);
      const { element, files } = createTablerImageElement(entry, svg);

      app.addElementsFromPasteOrLibrary({
        elements: [element],
        files,
        position: "center",
        retainSeed: false,
      });
      app.focusContainer();
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không chèn được icon");
    }
  };

  const insertSketchItem = (item: SketchItem) => {
    setError(null);
    app.addElementsFromPasteOrLibrary({
      elements: item.elements,
      files: null,
      position: "center",
      retainSeed: false,
    });
    app.focusContainer();
    close();
  };

  const insertIconText = (item: IconEntry) => {
    setError(null);

    const element = newTextElement({
      text: item.t,
      fontSize: 28,
      fontFamily: FONT_FAMILY.Helvetica,
      x: 0,
      y: 0,
    });

    app.addElementsFromPasteOrLibrary({
      elements: [element],
      files: null,
      position: "center",
      retainSeed: false,
    });
    app.focusContainer();
    close();
  };

  const onIconResultsScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const el = event.currentTarget;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
        setIconVisibleCount((prev) => prev + ICON_LOAD_MORE_STEP);
      }
    },
    [],
  );

  return (
    <div className="DrawLabIconPicker" ref={rootRef}>
      <button
        type="button"
        className={clsx(
          "ToolIcon_type_button ToolIcon_size_medium ToolIcon_type_button--show ToolIcon DrawLabIconPicker__trigger",
          { "ToolIcon--selected": isOpen },
        )}
        aria-label="Icon picker"
        title="Icon picker — /"
        aria-keyshortcuts=" /"
        onClick={(event) => {
          event.stopPropagation();
          editorJotaiStore.set(drawLabIconPickerOpenAtom, !isOpen);
        }}
      >
        <div
          className="ToolIcon__icon"
          aria-hidden="true"
          aria-disabled="false"
        >
          {DrawLabIconsIcon}
          <span className="ToolIcon__keybinding">/</span>
        </div>
      </button>

      {isOpen && (
        <div className="DrawLabIconPicker__panel" role="dialog">
          <div className="DrawLabIconPicker__tabs" role="tablist">
            <button
              type="button"
              className={clsx("DrawLabIconPicker__tab", {
                "DrawLabIconPicker__tab--active": activeTab === "tabler",
              })}
              onClick={() => {
                setActiveTab("tabler");
                setQuery("");
              }}
            >
              Tabler
            </button>
            <button
              type="button"
              className={clsx("DrawLabIconPicker__tab", {
                "DrawLabIconPicker__tab--active": activeTab === "sketch",
              })}
              onClick={() => {
                setActiveTab("sketch");
                setQuery("");
              }}
            >
              Sketch
            </button>
            <button
              type="button"
              className={clsx("DrawLabIconPicker__tab", {
                "DrawLabIconPicker__tab--active": activeTab === "icon",
              })}
              onClick={() => {
                setActiveTab("icon");
                setQuery("");
              }}
            >
              Icon
            </button>
          </div>

          <input
            ref={searchInputRef}
            className="DrawLabIconPicker__search"
            value={query}
            placeholder="Search"
            onChange={(event) => setQuery(event.target.value)}
          />

          <div className="DrawLabIconPicker__status" aria-live="polite">
            {isLoading && "Đang tải..."}
            {!isLoading && error}
            {!isLoading &&
              !error &&
              activeTab === "tabler" &&
              tablerIndex &&
              `${filteredTablerEntries.length} / ${tablerIndex.length} icons`}
            {!isLoading &&
              !error &&
              activeTab === "sketch" &&
              sketchItems &&
              `${filteredSketchItems.length} / ${sketchItems.length} items`}
            {!isLoading &&
              !error &&
              activeTab === "icon" &&
              iconData &&
              (() => {
                const total = iconSections.reduce(
                  (acc, s) => acc + s.items.length,
                  0,
                );
                return `${total} items`;
              })()}
          </div>

          <div
            className={clsx("DrawLabIconPicker__results", {
              "DrawLabIconPicker__results--icon": activeTab === "icon",
            })}
            onScroll={activeTab === "icon" ? onIconResultsScroll : undefined}
          >
            {activeTab === "tabler" &&
              filteredTablerEntries.map((entry) => (
                <button
                  type="button"
                  className="DrawLabIconPicker__result DrawLabIconPicker__result--tabler"
                  key={entry.id}
                  title={entry.label}
                  onClick={() => insertTablerIcon(entry)}
                >
                  <img
                    src={getAssetUrl(entry.file)}
                    alt=""
                    loading="lazy"
                    className="DrawLabIconPicker__preview"
                  />
                  <span>{entry.name}</span>
                </button>
              ))}

            {activeTab === "sketch" &&
              filteredSketchItems.map((item) => (
                <button
                  type="button"
                  className="DrawLabIconPicker__result DrawLabIconPicker__result--sketch"
                  key={item.id}
                  title={item.name}
                  onClick={() => insertSketchItem(item)}
                >
                  <SketchItemThumbnail id={item.id} elements={item.elements} />
                </button>
              ))}

            {activeTab === "icon" &&
              iconSections.map((section) => (
                <div
                  key={section.type}
                  className={clsx(
                    "DrawLabIconPicker__iconSection",
                    `DrawLabIconPicker__iconSection--${section.type}`,
                  )}
                >
                  <div className="DrawLabIconPicker__sectionHeader">
                    {section.label} · {section.items.length}
                  </div>
                  <div className="DrawLabIconPicker__iconGrid">
                    {section.items
                      .slice(0, iconVisibleCount)
                      .map((item, idx) => (
                        <button
                          type="button"
                          key={`${section.type}-${idx}`}
                          className="DrawLabIconPicker__iconResult"
                          title={item.n}
                          onClick={() => insertIconText(item)}
                        >
                          <span>{item.t}</span>
                        </button>
                      ))}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};
